
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import PModal from "@/components/look/PModal.vue";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import Multiselect from "vue-multiselect";
import { i18n } from "@/i18n";
import PickupForm from "@/components/orders/PickupForm.vue";
import AssistanceServiceForm from "@/components/orders/AssistanceServiceForm.vue";
import DeliveryForm from "@/components/orders/DeliveryForm.vue";
import SupplyMaterialForm from "@/components/orders/SupplyMaterialForm.vue";

import { Action, Getter } from "vuex-class";
import format from "date-fns/format";
import {
  IAssistanceService,
  ICountryConfig,
  IDeliveryService,
  IFault,
  IOrderConfigCommon,
  IPickUpService,
  IRequestedAmount,
  IServicepointLimitsData,
  ISolCustomerCommodityType,
  ISolCustomerCurrency,
  ISolCustomerDenomination,
  ISolCustomerDenominationPack,
  ISolCustomerDenominationQuantity,
  ISolCustomerPack,
  ISolCustomerPackQuantity,
  ISolCustomerSupply,
  ISolCustomerSupplyMaterialService,
  IUserClientData,
  IUserClientSubclientData,
  IUserClientSubclientServicepointData,
  IUserDetailData,
  PickUpService,
} from "@/entities";
import {
  ISolCustomerDenominationParams,
  ISolCustomerDenominationResponse,
  ISolCustomerFaultParams,
  ISolCustomerFaultResponse,
  ISolCustomerPackParams,
  ISolCustomerPackResponse,
  ISolCustomerResponse,
  ISolCustomerSupplyParams,
  ISolCustomerSupplyResponse,
} from "@/services";
import { BModal } from "bootstrap-vue";
import AsyncComputed from "vue-async-computed-decorator";
import { SweetAlertResult } from "sweetalert2";
import { MODULE } from "@/constans";

@Component({
  name: "new-order-modal",
  components: {
    PModal,
    ValidationObserver,
    ValidationProvider,
    PickupForm,
    AssistanceServiceForm,
    DeliveryForm,
    SupplyMaterialForm,
    Multiselect,
  },
})
export default class NewOrderModal extends Vue {
  @Getter("getLoggedUser") getLoggedUser!: IUserDetailData;
  @Getter("getSolCustomerCurrencies") solCustomerCurrencies!: ISolCustomerCurrency[];
  @Getter("getCountryConfig") countryConfig!: ICountryConfig | undefined;
  @Action("savePickUpService") savePickUpService!: (
    pickUpService: IPickUpService
  ) => Promise<ISolCustomerResponse | undefined>;
  @Action("saveAssistanceService") saveAssistanceService!: (
    assistanceService: IAssistanceService
  ) => Promise<ISolCustomerResponse | undefined>;
  @Action("saveDeliveryService") saveDeliveryService!: (
    deliveryService: IDeliveryService
  ) => Promise<ISolCustomerResponse | undefined>;
  @Action("saveSupplyMaterialService") saveSupplyMaterialService!: (
    supplyMaterialService: ISolCustomerSupplyMaterialService
  ) => Promise<ISolCustomerResponse | undefined>;
  @Action("fetchSolCustomerFaults") fetchSolCustomerFaults!: (
    params: ISolCustomerFaultParams
  ) => Promise<ISolCustomerFaultResponse | undefined>;
  @Action("fetchSolCustomerDenominations") fetchSolCustomerDenominations!: (
    params: ISolCustomerDenominationParams
  ) => Promise<ISolCustomerDenominationResponse | undefined>;
  @Action("fetchSolCustomerPacks") fetchSolCustomerPacks!: (
    params: ISolCustomerPackParams
  ) => Promise<ISolCustomerPackResponse | undefined>;
  @Action("fetchSolCustomerSupply") fetchSolCustomerSupply!: (
    params: ISolCustomerSupplyParams
  ) => Promise<ISolCustomerSupplyResponse | undefined>;
  @Action("fetchServicepointLimits") fetchServicepointLimits!: (
    idServicepoint: number
  ) => Promise<IServicepointLimitsData | undefined>;

  @Prop({ required: true })
  clientSelected: IUserClientData | undefined;
  @Prop({ required: true })
  subclientSelected: IUserClientSubclientData | undefined;
  @Prop({ required: true })
  servicepointSelected: IUserClientSubclientServicepointData | undefined;
  @Prop({ required: true, default: [] })
  clients: IUserClientData[] | undefined;
  @Prop({ required: true, default: [] })
  subclients: IUserClientSubclientData[] | undefined;
  @Prop({ required: true, default: [] })
  servicepoints: IUserClientSubclientServicepointData[] | undefined;
  @Prop({ required: true, default: [] })
  packs!: ISolCustomerPack[];

  datePattern = "yyyy-MM-dd'T'00:00:00XXX";
  dateTimePattern = "yyyy-MM-dd'T'HH:mm:ssXXX";
  requestType: { value: number; description: string } | null = null;
  serviceDate: Date = new Date();
  amount: number | null = null;
  comments = "";
  fault: IFault | null = null;
  origin = "MYPROSEGUR";
  currency: ISolCustomerCurrency | null =
    this.solCustomerCurrencies?.find((c) => c.mainCurrency === true) || null;
  denominationQuantities: ISolCustomerDenominationQuantity[] = [];
  packQuantities: ISolCustomerPackQuantity[] = [];
  supplies: ISolCustomerSupply[] = [];
  pickupInitialTime = this.isEconoCash
    ? `${this.pickupConfig?.EconoCash?.DefaultStartTime}:00:00`
    : `${this.pickupConfig?.DefaultStartTime}:00:00`;
  pickupFinalTime = this.isEconoCash
    ? `${this.pickupConfig?.EconoCash?.DefaultFinalTime}:00:00`
    : `${this.pickupConfig?.DefaultFinalTime}:00:00`;
  deliveryInitialTime = `${this.pickupConfig?.DefaultStartTime}:00:00`;
  deliveryFinalTime = `${this.pickupConfig?.DefaultFinalTime}:00:00`;
  assistanceInitialTime = `${this.assistanceConfig?.DefaultStartTime}:00:00`;
  assistanceFinalTime = `${this.assistanceConfig?.DefaultFinalTime}:00:00`;

  constructor() {
    super();
  }

  get isEconoCash(): boolean | undefined {
    return (
      this.servicepointSelected?.econoCash &&
      this.countryConfig?.Orders.Pickup.EconoCash !== undefined
    );
  }

  get pickupConfig(): IOrderConfigCommon | undefined {
    return this.countryConfig?.Orders.Pickup;
  }

  get deliveryConfig(): IOrderConfigCommon | undefined {
    return this.countryConfig?.Orders.Delivery;
  }

  get assistanceConfig(): IOrderConfigCommon | undefined {
    return this.countryConfig?.Orders.TechnicalAssistance;
  }

  initData(): void {
    this.requestType = null;
    this.serviceDate.setDate(new Date().getDate() + 1);
    this.amount = null;
    this.comments = "";
    this.fault = null;
    this.currency = this.mainCurrency || null;
    this.packQuantities?.forEach((p) => {
      if (p.packQuantity) {
        p.packQuantity = undefined;
      }
    });
    this.denominationQuantities?.forEach((d) => {
      if (d.denominationQuantity) {
        d.denominationQuantity = undefined;
      }
    });
    this.supplies = [];
    this.pickupInitialTime = this.isEconoCash
      ? `${this.pickupConfig?.EconoCash?.DefaultStartTime}:00:00`
      : `${this.pickupConfig?.DefaultStartTime}:00:00`;
    this.pickupFinalTime = this.isEconoCash
      ? `${this.pickupConfig?.EconoCash?.DefaultFinalTime}:00:00`
      : `${this.pickupConfig?.DefaultFinalTime}:00:00`;
    this.deliveryInitialTime = `${this.pickupConfig?.DefaultStartTime}:00:00`;
    this.deliveryFinalTime = `${this.pickupConfig?.DefaultFinalTime}:00:00`;
    this.assistanceInitialTime = `${this.assistanceConfig?.DefaultStartTime}:00:00`;
    this.assistanceFinalTime = `${this.assistanceConfig?.DefaultFinalTime}:00:00`;
  }

  onChangeRequestType(): void {
    if (this.requestType?.value === 2) {
      this.currency = this.mainCurrency || null;
    }
  }

  get optionsRequestType(): { value: number; description: string }[] {
    const options = [];

    if (
      this.$ability.can("manage", "Pickup") &&
      this.servicepointSelected?.adapters?.find((a) => a.code === MODULE.PICKUP)
    ) {
      options.push({ value: 0, description: i18n.t("label.pickup").toString() });
    }
    if (
      this.$ability.can("manage", "Delivery") &&
      this.servicepointSelected?.adapters?.find((a) => a.code === MODULE.DELIVERY)
    ) {
      options.push({ value: 1, description: i18n.t("label.delivery").toString() });
    }
    if (
      this.$ability.can("manage", "DeliveryExchange") &&
      this.servicepointSelected?.adapters?.find((a) => a.code === MODULE.DELIVERY_EXCHHANGE)
    ) {
      options.push({ value: 2, description: i18n.t("label.deliveryExchange").toString() });
    }
    if (
      this.$ability.can("manage", "AssistanceService") &&
      this.servicepointSelected?.adapters?.find((a) => a.code === MODULE.ASSISTANCE_SERVICE)
    ) {
      options.push({ value: 3, description: i18n.t("label.technicalAssistance").toString() });
    }
    if (
      this.$ability.can("manage", "SupplyMaterialService") &&
      this.servicepointSelected?.adapters?.find((a) => a.code === MODULE.SUPPLY_MATERIAL_SERVICE)
    ) {
      options.push({ value: 4, description: i18n.t("label.supplyMaterialService").toString() });
    }

    if (!options?.find((option) => option.value === this.requestType?.value)) {
      this.requestType = null;
    }

    return options;
  }

  get mainCurrency(): ISolCustomerCurrency | undefined {
    return this.solCustomerCurrencies?.find((c) => c.mainCurrency === true);
  }

  get _clientSelected(): IUserClientData | undefined {
    return this.clientSelected;
  }
  set _clientSelected(value: IUserClientData | undefined) {
    this.$emit("update:clientSelected", value);
  }
  get _subclientSelected(): IUserClientSubclientData | undefined {
    return this.subclientSelected;
  }
  set _subclientSelected(value: IUserClientSubclientData | undefined) {
    this.$emit("update:subclientSelected", value);
  }
  get _servicepointSelected(): IUserClientSubclientServicepointData | undefined {
    return this.servicepointSelected;
  }
  set _servicepointSelected(value: IUserClientSubclientServicepointData | undefined) {
    this.$emit("update:servicepointSelected", value);
    this.$emit("changeServicePoint", value);
    if (
      this.countryConfig?.Orders.Pickup.EconoCash &&
      this.servicepointSelected?.econoCash !== value?.econoCash
    ) {
      this.pickupInitialTime = value?.econoCash
        ? `${this.pickupConfig?.EconoCash?.DefaultStartTime}:00:00`
        : `${this.pickupConfig?.DefaultStartTime}:00:00`;
      this.pickupFinalTime = value?.econoCash
        ? `${this.pickupConfig?.EconoCash?.DefaultFinalTime}:00:00`
        : `${this.pickupConfig?.DefaultFinalTime}:00:00`;
    }
  }

  @AsyncComputed()
  async faults(): Promise<IFault[] | undefined> {
    if (this.clientSelected) {
      const params = {
        countryCode: this.getLoggedUser?.country?.code,
        entityId: this.clientSelected?.oidSol,
      } as ISolCustomerFaultParams;

      const res = await this.fetchSolCustomerFaults(params);

      return res?.faults || [];
    }
  }

  @Watch("packs")
  onChangePacks(): void {
    this.packQuantities = [];

    this.packs?.forEach((p) => {
      this.packQuantities.push({
        packId: p.id || "",
      });
    });
  }

  @AsyncComputed()
  async denominations(): Promise<ISolCustomerDenomination[] | undefined> {
    if (this.currency) {
      this.denominationQuantities = [];
      const params = {
        countryCode: this.getLoggedUser?.country?.code,
        currencyId: this.currency.currencyId,
      } as ISolCustomerDenominationParams;

      const res = await this.fetchSolCustomerDenominations(params);

      res?.denominations?.forEach((d) => {
        this.denominationQuantities.push({
          denominationId: d.id,
        });
      });

      return res?.denominations || [];
    }
  }

  @AsyncComputed()
  async commodityTypes(): Promise<ISolCustomerCommodityType[] | undefined> {
    const params = {
      countryCode: this.getLoggedUser?.country?.code,
    } as ISolCustomerSupplyParams;

    const res = await this.fetchSolCustomerSupply(params);

    return res?.commodityTypes || [];
  }

  async isLimitExceeded(): Promise<boolean> {
    const limits = await this.fetchServicepointLimits(this.servicepointSelected!.id!);
    let exceeded = false;

    limits?.limits?.every((limit) => {
      let amount = limit.isoCode === this.currency?.isoCode ? this.totalAmountDenominations() : 0;

      amount += this.totalAmountPacks(limit.isoCode);

      if (amount > (limit.amount || 0)) {
        this.$swal({
          title: this.$t("message.newOrder.limit.exceeded.title").toString(),
          text: this.$t("message.newOrder.limit.exceeded.description", [
            this.$options.filters?.currency(
              limit.amount,
              this.solCustomerCurrencies?.find((c) => c.isoCode === limit.isoCode)?.symbol ||
                limit.isoCode
            ),
            "",
          ]).toString(),
          icon: "warning",
          confirmButtonText: this.$t("label.ok").toString(),
        });
        exceeded = true;
      }
      return !exceeded;
    });
    return exceeded;
  }

  async onSubmit(): Promise<void> {
    if (
      (this.requestType?.value === 1 || this.requestType?.value === 2) &&
      (await this.isLimitExceeded())
    ) {
      return;
    }

    const { isConfirmed } = await this.modalConfirm();

    if (isConfirmed) {
      let res: ISolCustomerResponse | undefined;

      switch (this.requestType?.value) {
        case 0:
          res = await this.submitPickUpForm();
          break;
        case 1:
          res = await this.submitDeliveryForm(false);
          break;
        case 2:
          res = await this.submitDeliveryForm(true);
          break;
        case 3:
          res = await this.submitAssistanceServiceForm();
          break;
        case 4:
          res = await this.submitSupplyForm();
          break;
      }

      if (res) {
        this.makeToast("success", i18n.t("messages.newOrder.ok").toString());
        this.$emit("success");
        this.hideModal();
      } else {
        this.makeToast("warning", i18n.t("messages.newOrder.ko").toString());
      }
    }
  }

  async submitPickUpForm(): Promise<ISolCustomerResponse | undefined> {
    const pickUpService = {
      countryCode: this.getLoggedUser.country?.code,
      comments: this.comments || undefined,
      servicePointId: this.servicepointSelected?.oidSol,
      serviceDate: format(this.serviceDate, this.datePattern),
      isChange: false,
      origin: this.origin,
      hasDate: true,
      hasLimitDate: false,
      userName: `${this.getLoggedUser.name} ${this.getLoggedUser.lastName || ""}`.trim(),
    } as IPickUpService;

    if (!this.servicepointSelected?.agilePickup && this.amount) {
      pickUpService.requestedAmounts = [
        {
          currencyId: this.currency?.currencyId,
          totalAmount: Number(this.amount),
        } as IRequestedAmount,
      ];
    }

    if (this.pickupConfig?.AllowIntervalTime) {
      const requestInitialTime = new Date(this.serviceDate);
      const requestFinalTime = new Date(this.serviceDate);
      const initialHour = this.pickupInitialTime.split(":")[0];
      const initialMinute = this.pickupInitialTime.split(":")[1];
      const finalHour = this.pickupFinalTime.split(":")[0];
      const finalMinute = this.pickupFinalTime.split(":")[1];

      requestInitialTime.setHours(Number(initialHour));
      requestInitialTime.setMinutes(Number(initialMinute));
      requestInitialTime.setSeconds(0);

      requestFinalTime.setHours(Number(finalHour));
      requestFinalTime.setMinutes(Number(finalMinute));
      requestFinalTime.setSeconds(0);

      pickUpService.requestInitialTime = format(requestInitialTime, this.dateTimePattern);
      pickUpService.requestFinalTime = format(requestFinalTime, this.dateTimePattern);
    }

    return this.savePickUpService(new PickUpService(pickUpService));
  }

  async submitDeliveryForm(isChange: boolean): Promise<ISolCustomerResponse | undefined> {
    const deliveryService = {
      countryCode: this.getLoggedUser.country?.code,
      comments: this.comments || undefined,
      servicePointId: this.servicepointSelected?.oidSol,
      serviceDate: format(this.serviceDate, this.datePattern),
      isChange: isChange,
      origin: this.origin,
      hasDate: true,
      hasLimitDate: false,
      userName: `${this.getLoggedUser.name} ${this.getLoggedUser.lastName || ""}`.trim(),
      requestedAmounts: [],
    } as IDeliveryService;

    const denominations = this.denominations as any as ISolCustomerDenomination[] | undefined;
    if (denominations?.length) {
      deliveryService.requestedAmounts?.push({
        currencyId: this.currency?.currencyId,
        denominations: this.getDenominationsWithQuantity(this.denominationQuantities),
        packs: [],
        totalAmount: this.totalAmountDenominations(),
      } as IRequestedAmount);
    }

    const packs = this.packs as any as ISolCustomerPack[];

    for (const packWithQuantity of this.getPacksWithQuantity(this.packQuantities)) {
      const pack = packs.find((p) => p.id === packWithQuantity.packId);
      const denominationsPack = pack?.denominations || [];

      const currency = this.solCustomerCurrencies.find(
        (c) => c.isoCode === denominationsPack[0].currencyIsoCode
      );

      const requestedAmount = deliveryService.requestedAmounts?.find(
        (r) => r.currencyId === currency?.currencyId
      );

      if (requestedAmount) {
        requestedAmount.packs?.push(packWithQuantity);
      } else {
        deliveryService.requestedAmounts?.push({
          currencyId: currency?.currencyId,
          denominations: [],
          packs: [packWithQuantity],
          totalAmount: 0,
        } as IRequestedAmount);
      }
    }

    if (this.deliveryConfig?.AllowIntervalTime) {
      const requestInitialTime = new Date(this.serviceDate);
      const requestFinalTime = new Date(this.serviceDate);
      const initialHour = this.deliveryInitialTime.split(":")[0];
      const initialMinute = this.deliveryInitialTime.split(":")[1];
      const finalHour = this.deliveryFinalTime.split(":")[0];
      const finalMinute = this.deliveryFinalTime.split(":")[1];

      requestInitialTime.setHours(Number(initialHour));
      requestInitialTime.setMinutes(Number(initialMinute));
      requestInitialTime.setSeconds(0);

      requestFinalTime.setHours(Number(finalHour));
      requestFinalTime.setMinutes(Number(finalMinute));
      requestFinalTime.setSeconds(0);

      deliveryService.requestInitialTime = format(requestInitialTime, this.dateTimePattern);
      deliveryService.requestFinalTime = format(requestFinalTime, this.dateTimePattern);
    }

    return this.saveDeliveryService(deliveryService);
  }

  async submitAssistanceServiceForm(): Promise<ISolCustomerResponse | undefined> {
    const assistanceService = {
      countryCode: this.getLoggedUser.country?.code,
      comments: this.comments || undefined,
      servicePointId: this.servicepointSelected?.oidSol,
      serviceDate: format(this.serviceDate, this.datePattern),
      origin: this.origin,
      hasDate: true,
      hasLimitDate: false,
      faultDescription: this.fault?.faultDescription,
      userName: `${this.getLoggedUser.name} ${this.getLoggedUser.lastName || ""}`.trim(),
    } as IAssistanceService;

    if (this.assistanceConfig?.AllowIntervalTime) {
      const requestInitialTime = new Date(this.serviceDate);
      const requestFinalTime = new Date(this.serviceDate);
      const initialHour = this.assistanceInitialTime.split(":")[0];
      const initialMinute = this.assistanceInitialTime.split(":")[1];
      const finalHour = this.assistanceFinalTime.split(":")[0];
      const finalMinute = this.assistanceFinalTime.split(":")[1];

      requestInitialTime.setHours(Number(initialHour));
      requestInitialTime.setMinutes(Number(initialMinute));
      requestInitialTime.setSeconds(0);

      requestFinalTime.setHours(Number(finalHour));
      requestFinalTime.setMinutes(Number(finalMinute));
      requestFinalTime.setSeconds(0);

      assistanceService.requestInitialTime = format(requestInitialTime, this.dateTimePattern);
      assistanceService.requestFinalTime = format(requestFinalTime, this.dateTimePattern);
    }

    return this.saveAssistanceService(assistanceService);
  }

  async submitSupplyForm(): Promise<ISolCustomerResponse | undefined> {
    const supplyMaterialService = {
      countryCode: this.getLoggedUser.country?.code,
      servicePointId: this.servicepointSelected?.oidSol,
      origin: this.origin,
      hasDate: false,
      hasLimitDate: false,
      userName: `${this.getLoggedUser.name} ${this.getLoggedUser.lastName || ""}`.trim(),
      supplies: this.supplies,
    } as ISolCustomerSupplyMaterialService;

    return this.saveSupplyMaterialService(supplyMaterialService);
  }

  async modalConfirm(): Promise<SweetAlertResult> {
    return this.$swal({
      title: this.servicepointSelected?.flowApproval
        ? i18n.t("message.newOrder.confirm.approval.title").toString()
        : i18n.t("message.newOrder.confirm.title").toString(),
      html: this.servicepointSelected?.flowApproval
        ? i18n.t("message.newOrder.confirm.approval.description").toString()
        : i18n.t("message.newOrder.confirm.description").toString(),
      icon: "question",
      showCancelButton: true,
      confirmButtonText: i18n.t("label.yes").toString(),
      cancelButtonText: i18n.t("label.no").toString(),
    });
  }

  hideModal(): void {
    const pModal = this.$refs.newOrderModal as PModal;
    const bModal = pModal.$children[0] as BModal;
    bModal.hide();
  }

  makeToast(variant: string, message: string): void {
    this.$bvToast.toast(message, {
      autoHideDelay: 4000,
      variant,
      toaster: "b-toaster-bottom-center",
      noCloseButton: true,
    });
  }

  getDenominationsWithQuantity(
    denominations: ISolCustomerDenominationQuantity[]
  ): ISolCustomerDenominationQuantity[] {
    const denominationsWithQuantity: ISolCustomerDenominationQuantity[] = [];

    denominations.forEach((d) => (d.denominationQuantity ? denominationsWithQuantity.push(d) : ""));

    return denominationsWithQuantity;
  }

  getPacksWithQuantity(packs: ISolCustomerPackQuantity[]): ISolCustomerPackQuantity[] {
    const packsWithQuantity: ISolCustomerPackQuantity[] = [];

    packs.forEach((p) => (p.packQuantity ? packsWithQuantity.push(p) : ""));

    return packsWithQuantity;
  }

  totalAmountDenominations(): number {
    let totalAmount = 0;
    const denominations = this.denominations as any as ISolCustomerDenomination[];

    if (denominations) {
      denominations.forEach((denomination, i) => {
        if (
          denomination?.value &&
          this.denominationQuantities &&
          this.denominationQuantities[i]?.denominationQuantity
        ) {
          totalAmount +=
            denomination?.value * (this.denominationQuantities[i].denominationQuantity || 0);
        }
      });
    }

    return totalAmount;
  }

  totalAmountPacks(isoCode?: string): number {
    let totalAmount = 0;
    const packs = this.packs as any as ISolCustomerPack[];

    if (packs) {
      packs.forEach((pack, index) => {
        if (this.packQuantities && this.packQuantities[index]) {
          const denominations = isoCode
            ? pack.denominations?.filter(
                (d: ISolCustomerDenominationPack) => d.currencyIsoCode === isoCode
              )
            : pack.denominations;

          const packQuantity = this.packQuantities[index].packQuantity || 0;

          totalAmount += packQuantity * this.sumDenominations(denominations);
        }
      });
    }

    return totalAmount;
  }
  totalAmount(): number {
    return this.totalAmountDenominations() + this.totalAmountPacks();
  }

  sumDenominations(denominations: ISolCustomerDenominationPack[] | undefined): number {
    let amount = 0;

    denominations?.forEach(
      (denomination) => (amount += (denomination?.quantity || 0) * (denomination?.value || 0))
    );

    return amount;
  }
}
